import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Carrot,
  Eye,
  ImagePlus,
  Info,
  Layers,
  Loader2,
  Package,
  SlidersHorizontal,
  Tag,
} from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import imageService from "@/services/imageService";
import ingredientService from "@/services/ingredientService";
import productService from "@/services/productService";
import { showWarning } from "@/store/errorStore";
import {
  productFormSchema,
  productToFormValues,
  toProductPayload,
} from "@/schema/product/product";
import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  CountBadge,
  FieldError,
  FieldLabel,
  GroupTitle,
  SearchInput,
  SectionCard,
  SelectableList,
  TabButton,
  ToggleRow,
  fieldClass,
} from "./ProductFormParts";

const DEFAULT_VALUES = {
  name: "",
  description: "",
  price: "",
  category: "",
  image_url: "",
  available: true,
  min_stock: 0,

  creatingFlag: false, // cliente monta o próprio produto
  combo: false, // pode entrar como extra/combo em outros produtos

  // Aba Ingredientes
  hasExtraIngredientsFlag: false,
  totalIngredients: 0,
  weight: 0,
  unid: "",
  ingredientsSelectedIds: [], // ingredients (inclusos)
  extraIngredientsSelectedIds: [], // extraIngredients (GET /ingredients/extras)

  // Aba Extras
  hasExtraProductsFlag: false,
  totalProductsSelects: 0,
  weight_product: 0,
  unid_product: "",
  productsSelectedCodes: [], // products (itens do combo)
  extraProductsSelectedCodes: [], // extraProducts (GET /product-extras)
};

// Campos de cada aba, para abrir a aba certa quando a validação falhar
const TAB_FIELDS = {
  geral: ["name", "description", "price", "category", "image_url", "min_stock"],
  ingredientes: [
    "totalIngredients",
    "weight",
    "unid",
    "ingredientsSelectedIds",
    "extraIngredientsSelectedIds",
  ],
  extras: [
    "totalProductsSelects",
    "weight_product",
    "unid_product",
    "productsSelectedCodes",
    "extraProductsSelectedCodes",
  ],
};

const toFormValues = (product) =>
  product ? productToFormValues(product) : DEFAULT_VALUES;

/**
 * Junta as opções vindas da API com os itens já vinculados ao produto,
 * para que um item vinculado (ex.: ingrediente oculto depois) continue
 * aparecendo e possa ser desmarcado.
 */
function mergeOptions(options, linked, getKey) {
  const map = new Map();
  [...(options ?? []), ...(linked ?? [])].forEach((item) => {
    const id = String(getKey(item) ?? "");
    if (!id || map.has(id)) return;
    map.set(id, {
      id,
      name: String(item?.name ?? id),
      price: Number(item?.price ?? 0),
      qty: item?.qty,
      visible: item?.visible,
      available: item?.available,
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    a.name.localeCompare(b.name, "pt-BR")
  );
}

const normalize = (s) =>
  String(s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

const filterByName = (items, query) => {
  const q = normalize(query).trim();
  return q ? items.filter((i) => normalize(i.name).includes(q)) : items;
};

function PriceTag({ value, checked, plus = false }) {
  if (!value) {
    return (
      <span className="text-[12px] font-medium text-muted-foreground">
        Grátis
      </span>
    );
  }
  return (
    <span
      className={cn(
        "text-[12px] whitespace-nowrap",
        checked ? "font-bold text-brand" : "font-semibold text-muted-foreground"
      )}
    >
      {plus ? "+" : ""}
      {formatCurrency(value)}
    </span>
  );
}

function IncludedTag({ checked }) {
  if (!checked) return null;
  return (
    <span className="px-2 py-0.5 rounded bg-surface-container text-muted-foreground text-[11px] font-semibold">
      Incluso
    </span>
  );
}

/** Aviso exibido quando a seção da aba está desligada */
function DisabledHint({ children }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-surface-low p-4 text-center text-[13px] text-muted-foreground">
      {children}
    </div>
  );
}

function LoadingList({ text }) {
  return (
    <div className="flex items-center justify-center gap-2 py-4 text-[13px] text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      {text}
    </div>
  );
}

/** Tamanho / unidade / quantidade de escolha das abas Ingredientes e Extras */
function SizeFields({ register, prefix, totalField, weightField, unidField, totalTitle }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="flex flex-col gap-1">
        <FieldLabel htmlFor={`${prefix}-total`}>Qtd. escolha</FieldLabel>
        <input
          id={`${prefix}-total`}
          type="number"
          inputMode="numeric"
          min="0"
          title={totalTitle}
          {...register(totalField)}
          className={fieldClass}
        />
      </div>
      <div className="flex flex-col gap-1">
        <FieldLabel htmlFor={`${prefix}-weight`}>Tamanho</FieldLabel>
        <input
          id={`${prefix}-weight`}
          type="number"
          inputMode="numeric"
          min="0"
          {...register(weightField)}
          className={fieldClass}
        />
      </div>
      <div className="flex flex-col gap-1">
        <FieldLabel htmlFor={`${prefix}-unid`}>Unidade</FieldLabel>
        <input
          id={`${prefix}-unid`}
          placeholder="ml, g, un"
          {...register(unidField)}
          className={fieldClass}
        />
      </div>
    </div>
  );
}

export default function ProductFormModal({
  open,
  onOpenChange,
  editingProduct,
  onSave,
  categories = [],
  ingredientsCatalog = [],
  productsCatalog = [],
  loadingProduct = false,
}) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(productFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const [activeTab, setActiveTab] = useState("geral");
  const [ingredientQuery, setIngredientQuery] = useState("");
  const [productQuery, setProductQuery] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  const nameInputRef = useRef(null);

  // Opções de adicionais: carregadas sempre que o modal abre
  const [extraIngredientsOptions, setExtraIngredientsOptions] = useState([]);
  const [extraProductsOptions, setExtraProductsOptions] = useState([]);
  const [loadingExtras, setLoadingExtras] = useState(false);

  useEffect(() => {
    if (!open) return;
    let active = true;
    setLoadingExtras(true);
    Promise.allSettled([
      ingredientService.getExtras(),
      productService.getExtras(),
    ]).then(([ingredientsResult, productsResult]) => {
      if (!active) return;
      const listOf = (result) =>
        result.status === "fulfilled" && Array.isArray(result.value)
          ? result.value
          : [];
      setExtraIngredientsOptions(listOf(ingredientsResult));
      setExtraProductsOptions(listOf(productsResult));
      setLoadingExtras(false);
    });
    return () => {
      active = false;
    };
  }, [open]);

  // Preenche o formulário ao abrir (e quando chega o produto completo)
  useEffect(() => {
    if (!open) return;
    reset(toFormValues(editingProduct));
  }, [open, editingProduct, reset]);

  // Volta para a primeira aba e limpa as buscas a cada abertura
  useEffect(() => {
    if (!open) return;
    setActiveTab("geral");
    setIngredientQuery("");
    setProductQuery("");
  }, [open]);

  const values = watch();
  const productId = String(editingProduct?.id ?? "");

  const categoryNameById = useMemo(
    () => new Map(categories.map((c) => [String(c.id), c.name])),
    [categories]
  );
  const categoryName = categoryNameById.get(String(values.category)) ?? "";

  // Categoria de cada produto, para a dica das listas da aba Extras
  const productCategoryByCode = useMemo(
    () =>
      new Map(
        (productsCatalog ?? []).map((p) => [
          String(p.id),
          categoryNameById.get(String(p.category)),
        ])
      ),
    [productsCatalog, categoryNameById]
  );

  // ---- Ingredientes ----
  const includedIngredients = useMemo(
    () =>
      mergeOptions(
        ingredientsCatalog,
        editingProduct?.ingredients,
        (i) => i?.id
      ).map((i) => ({
        ...i,
        hint: i.visible === "N" ? "Oculto no cardápio" : undefined,
      })),
    [ingredientsCatalog, editingProduct]
  );

  const extraIngredients = useMemo(
    () =>
      mergeOptions(
        extraIngredientsOptions,
        editingProduct?.extraIngredients,
        (i) => i?.id
      ).map((i) => ({
        ...i,
        hint: i.visible === "N" ? "Oculto no cardápio" : "Adicional extra",
      })),
    [extraIngredientsOptions, editingProduct]
  );

  // ---- Produtos (o próprio produto nunca entra: ERRC084 / ERRC093) ----
  const comboProducts = useMemo(
    () =>
      mergeOptions(
        productsCatalog,
        editingProduct?.products,
        (p) => p?.code ?? p?.id
      )
        .filter((p) => p.id !== productId)
        .map((p) => ({
          ...p,
          hint:
            p.available === false
              ? "Indisponível"
              : productCategoryByCode.get(p.id),
        })),
    [productsCatalog, editingProduct, productId, productCategoryByCode]
  );

  const extraProducts = useMemo(
    () =>
      mergeOptions(
        extraProductsOptions,
        editingProduct?.extraProducts,
        (p) => p?.code ?? p?.id
      )
        .filter((p) => p.id !== productId)
        .map((p) => ({
          ...p,
          hint:
            p.qty != null && Number(p.qty) === 0
              ? "Sem estoque no momento"
              : productCategoryByCode.get(p.id),
        })),
    [extraProductsOptions, editingProduct, productId, productCategoryByCode]
  );

  const ingredientsCount =
    (values.ingredientsSelectedIds?.length ?? 0) +
    (values.extraIngredientsSelectedIds?.length ?? 0);
  const productsCount =
    (values.productsSelectedCodes?.length ?? 0) +
    (values.extraProductsSelectedCodes?.length ?? 0);

  const tabHasError = (tab) => TAB_FIELDS[tab].some((field) => errors[field]);

  /**
   * Envia a imagem para a biblioteca da empresa (POST /images/upload)
   * e usa a URL devolvida no produto
   */
  const handleImageFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const validationError = imageService.validateFile(file);
    if (validationError) {
      showWarning(validationError, "Imagem inválida");
      return;
    }

    setUploadingImage(true);
    try {
      const image = await imageService.upload(file, values.name || file.name);
      if (image?.url) {
        setValue("image_url", image.url, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    } catch (error) {
      // Modal global já exibido pelo interceptor
    } finally {
      setUploadingImage(false);
    }
  };

  const onSubmit = async (formValues) => {
    // Flags, composição (ingredients/products) e adicionais
    // (extraIngredients/extraProducts) já vêm montados do schema
    const payload = toProductPayload(formValues, includedIngredients);
    payload.available = Boolean(payload.available);
    payload.totalIngredients = Number(payload.totalIngredients ?? 0);
    payload.totalProductsSelects = Number(payload.totalProductsSelects ?? 0);
    payload.weight = Number(payload.weight ?? 0);
    payload.weight_product = Number(payload.weight_product ?? 0);
    // A empresa vem do token: company_code no payload só vale para o owner
    delete payload.company_code;

    const saved = await onSave?.(payload, editingProduct);
    if (saved === false) return;
    onOpenChange(false);
  };

  // Validação falhou: abre a aba do primeiro campo com erro
  const onInvalid = (formErrors) => {
    const tab = Object.keys(TAB_FIELDS).find((key) =>
      TAB_FIELDS[key].some((field) => formErrors[field])
    );
    if (tab) setActiveTab(tab);
  };

  const { ref: nameRef, ...nameField } = register("name");
  const busy = isSubmitting || uploadingImage || loadingProduct;

  return (
    <Dialog open={open} onOpenChange={(v) => !isSubmitting && onOpenChange(v)}>
      <DialogContent
        className="flex flex-col gap-0 p-0 w-full max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[92vh] overflow-hidden rounded-none sm:rounded-2xl bg-background border-border"
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          nameInputRef.current?.focus();
        }}
      >
        <form
          onSubmit={handleSubmit(onSubmit, onInvalid)}
          className="flex flex-col min-h-0 flex-1"
          noValidate
        >
          {/* Cabeçalho: título, status e abas */}
          <div className="px-4 sm:px-6 pt-4 pb-3 border-b border-border/70 bg-background">
            <div className="flex items-center justify-between gap-2 mb-3 pr-8">
              <div className="flex items-center gap-2 min-w-0">
                <Package className="h-5 w-5 text-brand flex-shrink-0" />
                <div className="min-w-0">
                  <DialogTitle className="font-display text-[20px] font-bold leading-tight text-foreground truncate">
                    {editingProduct ? "Editar Produto" : "Novo Produto"}
                  </DialogTitle>
                  <DialogDescription className="text-[12px] text-muted-foreground truncate">
                    {[values.name?.trim() || "Sem nome", categoryName]
                      .filter(Boolean)
                      .join(" • ")}
                  </DialogDescription>
                </div>
              </div>
              <span
                className={cn(
                  "px-2.5 py-1 rounded-full border text-[11px] font-bold flex items-center gap-1.5 flex-shrink-0",
                  values.available
                    ? "bg-brand-pistachio/20 border-brand-pistachio/40 text-brand"
                    : "bg-surface-container border-border text-muted-foreground"
                )}
              >
                <span
                  className={cn(
                    "h-2 w-2 rounded-full",
                    values.available
                      ? "bg-brand animate-pulse"
                      : "bg-muted-foreground"
                  )}
                />
                {values.available ? "Ativo" : "Inativo"}
              </span>
            </div>

            <div
              role="tablist"
              className="flex items-center gap-1.5 p-1 bg-surface-container rounded-xl border border-border"
            >
              <TabButton
                active={activeTab === "geral"}
                icon={Info}
                label="Geral"
                hasError={tabHasError("geral")}
                onClick={() => setActiveTab("geral")}
              />
              <TabButton
                active={activeTab === "ingredientes"}
                icon={Carrot}
                label="Ingredientes"
                hasError={tabHasError("ingredientes")}
                onClick={() => setActiveTab("ingredientes")}
              />
              <TabButton
                active={activeTab === "extras"}
                icon={Tag}
                label="Extras"
                hasError={tabHasError("extras")}
                onClick={() => setActiveTab("extras")}
              />
            </div>
          </div>

          {/* Conteúdo das abas (todas montadas para manter foco e validação) */}
          <div className="relative flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4">
            {loadingProduct && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-background/80 text-[13px] text-muted-foreground">
                <Loader2 className="h-6 w-6 animate-spin text-brand" />
                Carregando dados do produto...
              </div>
            )}

            {/* ABA 1: GERAL */}
            <div
              role="tabpanel"
              className={cn(
                "flex-col gap-3.5",
                activeTab === "geral" ? "flex" : "hidden"
              )}
            >
              {/* Foto + nome + preço */}
              <SectionCard className="p-3.5 flex gap-3.5 items-center">
                <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-surface border border-border">
                  {values.image_url ? (
                    <img
                      src={values.image_url}
                      alt="Foto do produto"
                      className="w-full h-full object-cover"
                    />
                  ) : null}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    title="Enviar imagem (JPG, PNG ou WEBP, até 5 MB)"
                    className={cn(
                      "absolute inset-0 flex flex-col items-center justify-center gap-0.5 transition-colors",
                      values.image_url
                        ? "bg-[#1c1c16]/30 text-white hover:bg-[#1c1c16]/45"
                        : "text-brand hover:bg-surface-container"
                    )}
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : values.image_url ? (
                      <Camera className="h-5 w-5" />
                    ) : (
                      <ImagePlus className="h-5 w-5" />
                    )}
                    <span className="text-[9px] font-bold uppercase tracking-tight">
                      {uploadingImage
                        ? "Enviando"
                        : values.image_url
                        ? "Alterar"
                        : "Enviar foto"}
                    </span>
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={handleImageFile}
                  />
                </div>

                <div className="flex flex-col flex-1 min-w-0">
                  <div className="flex justify-between items-start gap-1">
                    <span
                      className={cn(
                        "text-[11px] font-bold uppercase tracking-wider",
                        values.available ? "text-brand" : "text-muted-foreground"
                      )}
                    >
                      {values.available ? "Produto em venda" : "Fora do cardápio"}
                    </span>
                    {categoryName && (
                      <span className="text-[11px] font-bold text-muted-foreground truncate">
                        {categoryName}
                      </span>
                    )}
                  </div>
                  <label htmlFor="product-name" className="sr-only">
                    Nome do produto
                  </label>
                  <input
                    id="product-name"
                    placeholder="Nome do item"
                    autoComplete="off"
                    {...nameField}
                    ref={(el) => {
                      nameRef(el);
                      nameInputRef.current = el;
                    }}
                    className="w-full font-display text-[16px] font-bold text-foreground bg-transparent border-b border-border focus:border-brand focus:outline-none py-1 leading-tight placeholder:font-medium placeholder:text-muted-foreground"
                  />
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
                    <div className="flex items-baseline gap-1">
                      <label
                        htmlFor="product-price"
                        className="text-[12px] font-bold text-brand"
                      >
                        R$
                      </label>
                      <input
                        id="product-price"
                        type="number"
                        inputMode="decimal"
                        step="0.01"
                        min="0"
                        placeholder="0,00"
                        {...register("price")}
                        className="w-20 font-display text-[17px] font-bold text-foreground bg-transparent border-b border-border focus:border-brand focus:outline-none py-0.5"
                      />
                    </div>
                    <span className="text-[#c0c9bb]" aria-hidden="true">
                      •
                    </span>
                    <div className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
                      <label htmlFor="product-min-stock">Mín:</label>
                      <input
                        id="product-min-stock"
                        type="number"
                        inputMode="numeric"
                        min="0"
                        step="1"
                        title="Estoque mínimo (alerta de estoque baixo)"
                        {...register("min_stock")}
                        className="w-12 text-center font-bold text-foreground bg-surface rounded border border-border py-0.5 focus:border-brand focus:outline-none"
                      />
                      <span>un</span>
                    </div>
                  </div>
                  <FieldError error={errors.name} />
                  <FieldError error={errors.price} />
                  <FieldError error={errors.min_stock} />
                </div>
              </SectionCard>

              {/* Categoria, imagem e descrição */}
              <SectionCard className="p-4 flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="flex flex-col gap-1">
                    <FieldLabel htmlFor="product-category">Categoria</FieldLabel>
                    <select
                      id="product-category"
                      {...register("category")}
                      className={cn(fieldClass, "appearance-none pr-8 bg-no-repeat")}
                      style={{
                        backgroundImage:
                          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2341493e' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
                        backgroundPosition: "right 0.6rem center",
                      }}
                    >
                      <option value="">Selecione...</option>
                      {categories.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <FieldError error={errors.category} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <FieldLabel htmlFor="product-image">URL da imagem</FieldLabel>
                    <input
                      id="product-image"
                      placeholder="https://... ou /assets/arquivo.png"
                      {...register("image_url")}
                      className={cn(fieldClass, "truncate")}
                    />
                    <FieldError error={errors.image_url} />
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <FieldLabel htmlFor="product-description">
                    Descrição no cardápio
                  </FieldLabel>
                  <textarea
                    id="product-description"
                    rows={3}
                    {...register("description")}
                    className={cn(fieldClass, "resize-none leading-relaxed")}
                  />
                  <FieldError error={errors.description} />
                </div>
              </SectionCard>

              {/* Opções do produto */}
              <SectionCard className="p-3 flex flex-col divide-y divide-surface-container">
                <Controller
                  control={control}
                  name="available"
                  render={({ field }) => (
                    <ToggleRow
                      id="product-available"
                      icon={Eye}
                      title="Disponível no cardápio"
                      description="Item visível e aberto para pedidos"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="creatingFlag"
                  render={({ field }) => (
                    <ToggleRow
                      id="product-creating"
                      icon={SlidersHorizontal}
                      title="Permite personalização"
                      description="Cliente monta o próprio produto escolhendo os itens"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="combo"
                  render={({ field }) => (
                    <ToggleRow
                      id="product-combo"
                      icon={Layers}
                      title="Elegível para combos"
                      description="Pode ser oferecido como extra em outros produtos"
                      checked={field.value}
                      onChange={field.onChange}
                    />
                  )}
                />
              </SectionCard>
            </div>

            {/* ABA 2: INGREDIENTES */}
            <div
              role="tabpanel"
              className={cn(
                "flex-col gap-3",
                activeTab === "ingredientes" ? "flex" : "hidden"
              )}
            >
              <SectionCard className="p-3.5 flex flex-col gap-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="font-display text-[15px] font-bold text-foreground">
                      Ingredientes do item
                    </h3>
                    <p className="text-[11px] text-muted-foreground">
                      Marque os inclusos ou adicione opcionais
                    </p>
                  </div>
                  {values.hasExtraIngredientsFlag && (
                    <CountBadge
                      count={ingredientsCount}
                      singular="selecionado"
                      plural="selecionados"
                    />
                  )}
                </div>
                <div className="border-t border-surface-container">
                  <Controller
                    control={control}
                    name="hasExtraIngredientsFlag"
                    render={({ field }) => (
                      <ToggleRow
                        id="product-has-ingredients"
                        icon={Carrot}
                        title="Usar ingredientes"
                        description="Exibe os inclusos e os adicionais deste item"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                {values.hasExtraIngredientsFlag && (
                  <>
                    <SizeFields
                      register={register}
                      prefix="product-ingredients"
                      totalField="totalIngredients"
                      weightField="weight"
                      unidField="unid"
                      totalTitle="Quantos ingredientes o cliente pode escolher"
                    />
                    <FieldError error={errors.totalIngredients} />
                    <FieldError error={errors.weight} />
                    <SearchInput
                      value={ingredientQuery}
                      onChange={setIngredientQuery}
                      placeholder="Buscar ingrediente..."
                    />
                  </>
                )}
              </SectionCard>

              {values.hasExtraIngredientsFlag ? (
                <>
                  <GroupTitle
                    title="Inclusos"
                    description="Fazem parte do item"
                    count={values.ingredientsSelectedIds?.length ?? 0}
                  />
                  <Controller
                    control={control}
                    name="ingredientsSelectedIds"
                    render={({ field }) => (
                      <SelectableList
                        idPrefix="ingredient"
                        items={filterByName(includedIngredients, ingredientQuery)}
                        value={field.value}
                        onChange={field.onChange}
                        renderTag={(_, checked) => (
                          <IncludedTag checked={checked} />
                        )}
                        emptyText={
                          ingredientQuery
                            ? "Nenhum ingrediente encontrado"
                            : "Nenhum ingrediente cadastrado"
                        }
                      />
                    )}
                  />

                  <GroupTitle
                    title="Adicionais"
                    description="O cliente pode acrescentar; o preço vem do cadastro do ingrediente"
                    count={values.extraIngredientsSelectedIds?.length ?? 0}
                  />
                  {loadingExtras ? (
                    <LoadingList text="Carregando ingredientes..." />
                  ) : (
                    <Controller
                      control={control}
                      name="extraIngredientsSelectedIds"
                      render={({ field }) => (
                        <SelectableList
                          idPrefix="extra-ingredient"
                          items={filterByName(extraIngredients, ingredientQuery)}
                          value={field.value}
                          onChange={field.onChange}
                          renderTag={(item, checked) => (
                            <PriceTag value={item.price} checked={checked} plus />
                          )}
                          emptyText={
                            ingredientQuery
                              ? "Nenhum adicional encontrado"
                              : "Nenhum ingrediente visível para usar como adicional"
                          }
                        />
                      )}
                    />
                  )}
                </>
              ) : (
                <DisabledHint>
                  Ative <strong>Usar ingredientes</strong> para escolher os
                  ingredientes inclusos e os adicionais deste produto.
                </DisabledHint>
              )}
            </div>

            {/* ABA 3: EXTRAS (combo e produtos adicionais) */}
            <div
              role="tabpanel"
              className={cn(
                "flex-col gap-3",
                activeTab === "extras" ? "flex" : "hidden"
              )}
            >
              <SectionCard className="p-3.5 flex flex-col gap-2.5">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <h3 className="font-display text-[15px] font-bold text-foreground">
                      Combo e adicionais
                    </h3>
                    <p className="text-[11px] text-muted-foreground">
                      Itens do combo e sugestões oferecidas junto com o produto
                    </p>
                  </div>
                  {values.hasExtraProductsFlag && (
                    <CountBadge
                      count={productsCount}
                      singular="vinculado"
                      plural="vinculados"
                    />
                  )}
                </div>
                <div className="border-t border-surface-container">
                  <Controller
                    control={control}
                    name="hasExtraProductsFlag"
                    render={({ field }) => (
                      <ToggleRow
                        id="product-has-products"
                        icon={Tag}
                        title="Usar produtos extras"
                        description="Monta combos e oferece produtos adicionais"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </div>
                {values.hasExtraProductsFlag && (
                  <>
                    <SizeFields
                      register={register}
                      prefix="product-products"
                      totalField="totalProductsSelects"
                      weightField="weight_product"
                      unidField="unid_product"
                      totalTitle="Quantos produtos do combo o cliente pode escolher"
                    />
                    <FieldError error={errors.totalProductsSelects} />
                    <FieldError error={errors.weight_product} />
                    <SearchInput
                      value={productQuery}
                      onChange={setProductQuery}
                      placeholder="Filtrar itens do cardápio..."
                    />
                  </>
                )}
              </SectionCard>

              {values.hasExtraProductsFlag ? (
                <>
                  <GroupTitle
                    title="Itens do combo"
                    description="O cliente escolhe entre estes produtos"
                    count={values.productsSelectedCodes?.length ?? 0}
                  />
                  <Controller
                    control={control}
                    name="productsSelectedCodes"
                    render={({ field }) => (
                      <SelectableList
                        idPrefix="combo-product"
                        items={filterByName(comboProducts, productQuery)}
                        value={field.value}
                        onChange={field.onChange}
                        renderTag={(item, checked) => (
                          <PriceTag value={item.price} checked={checked} />
                        )}
                        emptyText={
                          productQuery
                            ? "Nenhum produto encontrado"
                            : "Nenhum outro produto cadastrado"
                        }
                      />
                    )}
                  />

                  <GroupTitle
                    title="Produtos adicionais"
                    description="Somente produtos marcados como “Elegível para combos”"
                    count={values.extraProductsSelectedCodes?.length ?? 0}
                  />
                  {loadingExtras ? (
                    <LoadingList text="Carregando produtos..." />
                  ) : (
                    <Controller
                      control={control}
                      name="extraProductsSelectedCodes"
                      render={({ field }) => (
                        <SelectableList
                          idPrefix="extra-product"
                          items={filterByName(extraProducts, productQuery)}
                          value={field.value}
                          onChange={field.onChange}
                          renderTag={(item, checked) => (
                            <PriceTag value={item.price} checked={checked} plus />
                          )}
                          emptyText={
                            productQuery
                              ? "Nenhum adicional encontrado"
                              : "Nenhum produto elegível para combos"
                          }
                        />
                      )}
                    />
                  )}
                </>
              ) : (
                <DisabledHint>
                  Ative <strong>Usar produtos extras</strong> para montar o
                  combo e escolher os produtos adicionais.
                </DisabledHint>
              )}
            </div>
          </div>

          {/* Rodapé fixo */}
          <div className="flex items-center justify-end gap-2 px-4 sm:px-6 py-3 border-t border-border bg-surface-low shadow-ambient">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="h-10 px-5 rounded-full border border-[#d5d3c5] bg-surface-low text-brand text-[13px] font-semibold hover:bg-surface-container transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy}
              className="h-10 px-6 rounded-full bg-brand text-white text-[13px] font-bold hover:bg-brand-dark transition-colors shadow-sm flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
              {isSubmitting
                ? "Salvando..."
                : editingProduct
                ? "Salvar alterações"
                : "Cadastrar produto"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
